import { useModalVisualViewport } from '../lib/useModalVisualViewport'
import { RacquetRangeControl } from '../components/PlayingLevel'
import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import StableFacilityImage from '../components/StableFacilityImage'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Clock, Shield, Users } from 'lucide-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { formatSportTypeLabel, MEMBER_RACQUET_SPORTS } from '../api/racquetCourts'
import BackPillButton from '../components/BackPillButton'
import MemberPaymentMethodOptions from '../components/MemberPaymentMethodOptions'
import { createPayNowHandoff, openPayNowCheckoutPlaceholder, subscribePayNowCompleted, updatePayNowHandoff } from '../utils/payNowTabHandoff'
import { commitMemberBookingSuccess, createCommittedBooking } from '../lib/upcomingBookingsCache'
import { formatCurrencyAmount } from '@/utils/currency'
import {
  deriveMemberPortalRacquetDurationOptions,
  getPreferredMemberPortalRacquetDuration,
  isValidMemberPortalRacquetDurationSelection,
} from '../utils/racquetDurationOptions'
import { getMemberRacquetBookingError } from '../utils/racquetBookingErrors'

function startOfDay(date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

function parseDateParam(value) {
  if (!value) return null
  const match = /^(-?\d{4})-(\d{2})-(\d{2})$/.exec(String(value))
  if (match) {
    const [, yStr, mStr, dStr] = match
    const parsed = new Date(Number.parseInt(yStr, 10), Number.parseInt(mStr, 10) - 1, Number.parseInt(dStr, 10))
    if (!Number.isNaN(parsed.getTime())) return startOfDay(parsed)
  }
  const fallback = new Date(value)
  return Number.isNaN(fallback.getTime()) ? null : startOfDay(fallback)
}

function formatDateParam(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function parseTimeToMinutes(value) {
  if (!value) return null
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const str = String(value)
  if (str.includes(':')) {
    const [hStr, mStr] = str.split(':')
    const h = Number.parseInt(hStr, 10)
    const m = Number.parseInt(mStr, 10)
    if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m
  }
  const parsed = Number.parseInt(str, 10)
  return Number.isFinite(parsed) ? parsed : null
}

function minutesToDate(date, minutes) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null
  const base = startOfDay(date)
  return new Date(base.getTime() + minutes * 60 * 1000)
}

function formatDateLabel(date, locale, memberText) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return memberText("portalPresentation.member_select_date")
  return date.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' })
}

function formatTimeLabel(date, locale, memberText) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return memberText("portalPresentation.member_select_time")
  return date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
}

function formatDuration(duration, locale, memberText) {
  const safe = Number.parseInt(duration, 10)
  if (!Number.isFinite(safe)) return memberText("portalPresentation.member_duration")
  return new Intl.NumberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'long' }).format(safe)
}

function diffMinutes(start, end) {
  if (!(start instanceof Date) || Number.isNaN(start.getTime())) return null
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return null
  return Math.max((end.getTime() - start.getTime()) / (60 * 1000), 0)
}

function buildPlayer(defaults = {}) {
  return {
    name: '',
    member_id: null,
    type: 'member',
    guestEmail: '',
    guestPhone: '',
    locked: false,
    ...defaults,
  }
}

function normalizeUuidLike(value) {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed) return null
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(trimmed)
    ? trimmed
    : null
}

function resolveMemberId(member) {
  return (
    normalizeUuidLike(member?.id) ||
    normalizeUuidLike(member?.member_id) ||
    normalizeUuidLike(member?.memberId) ||
    normalizeUuidLike(member?.profile?.id) ||
    null
  )
}

function getRacquetMaxPlayersClient(courtSize) {
  return String(courtSize || '').trim().toLowerCase() === 'double' ? 4 : 2
}

function MobileRacquetBookingWorkspace({
  backLink, summary, sportLabel, sportType, court, selectedDuration, bookingKind, setBookingKind,
  openMatchType, setOpenMatchType, openMatchJoinPolicy, setOpenMatchJoinPolicy,
  openMatchNote, setOpenMatchNote, skillMin, setSkillMin, skillMax, setSkillMax, activePlayers, maxPlayers, removePlayer, openPlayerSheet,
  addons, selectedAddonsById, incrementAddon, decrementAddon, formatPrice,
  paymentMethod, setPaymentMethod, saving, quoteLoading, authoritativeDisplayTotal,
  validationErrors, submitError, onViewOtherTimes, handleSubmit, playerSheet,
  closePlayerSheet, playerSheetMode, setPlayerSheetMode, playerDraft, setPlayerDraft,
  memberSuggestions, memberSearchLoading, onPlayerSearch, addSheetMember, addSheetGuest,
  allowGuests, facilityImage,
}) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const playerSheetRef = useRef(null)
  useModalVisualViewport(playerSheetRef, playerSheet)

  const occupiedCount = activePlayers.filter((player) => player?.name?.trim()).length
  const openPlaces = Math.max(0, maxPlayers - occupiedCount)
  const courtMetadata = [court?.is_outdoor ? memberText("racquetPlay.outdoor") : memberText("racquetPlay.indoor"), court?.surface, sportLabel].filter(Boolean).join(' · ')
  const totalLabel = quoteLoading ? memberText("golf.starter.updating") : authoritativeDisplayTotal == null ? memberText("portalPresentation.unavailable") : formatPrice(authoritativeDisplayTotal)
  const submitLabel = saving
    ? paymentMethod === 'pay_now' ? memberText("portalPresentation.member_processing_payment") : bookingKind === 'open_match' ? memberText("portalPresentation.member_creating_match") : memberText("portalPresentation.member_reserving_court")
    : paymentMethod === 'pay_now' && authoritativeDisplayTotal != null
      ? memberText("portalPresentation.member_pay_amount", { amount: formatPrice(authoritativeDisplayTotal) })
      : bookingKind === 'open_match' ? memberText("portalPresentation.member_create_open_match") : memberText("portalPresentation.member_book_court")
  const submitDisabled = saving || quoteLoading || authoritativeDisplayTotal == null || validationErrors.length > 0

  return (
    <form className="mp-racquet-mobile-workspace" onSubmit={handleSubmit} data-mobile-racquet-checkout>
      <div className="mp-racquet-mobile-content">
      <header className="mp-racquet-mobile-context">
        <BackPillButton to={backLink}>{memberText("portalPresentation.member_back")}</BackPillButton>
        <p className="mp-eyebrow">{sportLabel} {memberText("portalPresentation.member_court")}</p>
        <h1>{summary.courtName}</h1>
        <p className="mp-racquet-mobile-context__time">{summary.dateLabel} · {summary.timeLabel}</p>
        <div className="mp-racquet-mobile-context__meta"><strong>{selectedDuration} {memberText("portalPresentation.member_min")}</strong><span>{courtMetadata}</span></div>
      </header>

      <section className="mp-racquet-review-photo">
        <div className="mp-tee-booking__image mp-facility-review-image"><StableFacilityImage key={court?.id} identity={court?.id} url={court?.photo_url || facilityImage} /></div>
        <div className="mp-tee-booking__compact-summary"><strong>{summary.courtName}</strong><span>{summary.dateLabel} · {summary.timeLabel} · {selectedDuration} {memberText("portalPresentation.member_min")}</span></div>
      </section>

      <section className="mp-racquet-mobile-section" aria-labelledby="mobile-booking-type">
        <h2 id="mobile-booking-type">{memberText("portalPresentation.member_booking_type")}</h2>
        <div className="mp-racquet-mobile-segment" role="radiogroup" aria-label={memberText("portalPresentation.member_booking_type")}>
          <button type="button" role="radio" aria-checked={bookingKind === 'private'} className={bookingKind === 'private' ? 'is-selected' : ''} onClick={() => setBookingKind('private')}>{memberText("portalPresentation.member_private_booking")}</button>
          <button type="button" role="radio" aria-checked={bookingKind === 'open_match'} className={bookingKind === 'open_match' ? 'is-selected' : ''} onClick={() => setBookingKind('open_match')}>{memberText("portalPresentation.member_open_match")}</button>
        </div>
      </section>

      {bookingKind === 'open_match' ? (
        <section className="mp-racquet-mobile-section mp-racquet-mobile-open-match" aria-labelledby="mobile-open-match">
          <h2 id="mobile-open-match">{memberText("portalPresentation.member_open_match")}</h2>
          <label><span>{memberText("portalPresentation.member_match_type")}</span><div className="mp-racquet-mobile-segment compact"><button type="button" className={openMatchType === 'friendly' ? 'is-selected' : ''} onClick={() => setOpenMatchType('friendly')}>{memberText("portalPresentation.member_friendly")}</button><button type="button" className={openMatchType === 'competitive' ? 'is-selected' : ''} onClick={() => setOpenMatchType('competitive')}>{memberText("portalPresentation.member_competitive")}</button></div></label>
          <label><span>{memberText("portalPresentation.member_who_can_join")}</span><div className="mp-racquet-mobile-segment compact"><button type="button" className={openMatchJoinPolicy === 'approval_required' ? 'is-selected' : ''} onClick={() => setOpenMatchJoinPolicy('approval_required')}>{memberText("portalPresentation.member_host_approval")}</button><button type="button" className={openMatchJoinPolicy === 'direct_join' ? 'is-selected' : ''} onClick={() => setOpenMatchJoinPolicy('direct_join')}>{memberText("portalPresentation.member_direct_join")}</button></div></label>
          <RacquetRangeControl sport={sportType} min={skillMin} max={skillMax} onMinChange={setSkillMin} onMaxChange={setSkillMax} /><label className="mp-racquet-mobile-field"><span>{memberText("portalPresentation.member_optional_note")}</span><input value={openMatchNote} maxLength={180} onChange={(event) => setOpenMatchNote(event.target.value)} placeholder={memberText("portalPresentation.member_anything_players_should_know")} /></label>
        </section>
      ) : null}

      <section className="mp-racquet-mobile-section" aria-labelledby="mobile-players">
        <div className="mp-racquet-mobile-section__heading"><h2 id="mobile-players">{memberText("portalPresentation.member_players_caption")}</h2>{bookingKind === 'open_match' ? <span>{memberText("portalPresentation.member_places_available_count", { count: openPlaces })}</span> : null}</div>
        <div className="mp-racquet-player-slots" style={{ '--racquet-capacity': maxPlayers }}>
          {Array.from({ length: maxPlayers }).map((_, index) => {
            const player = activePlayers[index]
            if (!player) return <button type="button" className="mp-racquet-player-slot is-empty" key={`empty-${index}`} onClick={openPlayerSheet}><span>+</span>{memberText("portalPresentation.member_add_player")}</button>
            return <article className={`mp-racquet-player-slot${index === 0 ? ' is-primary' : ''}`} key={`player-${index}`}>
              <div className="mp-racquet-player-slot__avatar" aria-hidden>{String(player.name || 'P').trim().charAt(0).toUpperCase()}</div>
              <div><strong>{player.nameIsFallback ? memberText("portalPresentation.member_primary_member") : player.name}</strong><small>{index === 0 ? memberText("portalPresentation.member_host_member") : player.type === 'guest' ? memberText("portalPresentation.member_guest") : memberText("portalPresentation.member_member")}</small></div>
              {index > 0 ? <button type="button" aria-label={memberText("portalPresentation.member_remove_name", { name: player.name })} onClick={() => removePlayer(index)}>×</button> : null}
            </article>
          })}
        </div>
      </section>

      {addons.length ? <section className="mp-racquet-mobile-section" aria-labelledby="mobile-addons"><h2 id="mobile-addons">{memberText("portalPresentation.member_add_to_your_booking")}</h2><div className="mp-racquet-mobile-addons">{addons.map((addon) => { const qty = Number.parseInt(selectedAddonsById?.[addon.id], 10) || 0; return <div className="mp-racquet-mobile-addon" key={addon.id}><div><strong>{addon.label || addon.name}</strong><small>{addon.price != null ? formatPrice(addon.price) : memberText("portalPresentation.member_live_price")}</small></div><div className="mp-racquet-mobile-stepper"><button type="button" aria-label={memberText("portalPresentation.member_remove_name", { name: addon.label || addon.name })} disabled={qty === 0} onClick={() => decrementAddon(addon.id)}>−</button><span aria-live="polite">{qty}</span><button type="button" aria-label={memberText("portalPresentation.member_add_name", { name: addon.label || addon.name })} onClick={() => incrementAddon(addon.id)}>+</button></div></div> })}</div></section> : null}

      <section className="mp-racquet-mobile-section mp-racquet-mobile-payment" aria-labelledby="mobile-payment"><h2 id="mobile-payment">{memberText("facilityBooking.paymentLabel")}</h2><MemberPaymentMethodOptions value={paymentMethod} onChange={setPaymentMethod} disabled={saving} mobileCards /></section>

      {validationErrors.length > 0 ? <div className="mp-racquet-mobile-alert" role="alert">{validationErrors.map((message) => <p key={message}>{memberText(message)}</p>)}</div> : null}
      {submitError ? <div className="mp-racquet-mobile-alert" role="alert"><p>{memberText(submitError.messageKey || submitError.message)}</p>{submitError.canRefreshAvailability ? <button type="button" onClick={onViewOtherTimes}>{memberText("portalPresentation.member_view_other_times")}</button> : null}</div> : null}

      </div>
      <div className="mp-racquet-transaction-bar" aria-busy={quoteLoading || saving}><div><span>{memberText("portalPresentation.member_total")}</span><strong>{totalLabel}</strong></div><button type="submit" disabled={submitDisabled}>{submitLabel}</button></div>

      {playerSheet ? <div ref={playerSheetRef} className="mp-racquet-player-sheet-backdrop" onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); closePlayerSheet() } }} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closePlayerSheet() }}><section className="mp-racquet-player-sheet" role="dialog" aria-modal="true" aria-labelledby="player-sheet-title"><div className="mp-racquet-player-sheet__handle" aria-hidden /><header><h2 id="player-sheet-title">{memberText("portalPresentation.member_add_player")}</h2><button type="button" aria-label={memberText("portalPresentation.member_close_player_sheet")} onClick={closePlayerSheet}>×</button></header><div className="mp-racquet-mobile-segment" role="tablist"><button type="button" role="tab" aria-selected={playerSheetMode === 'member'} className={playerSheetMode === 'member' ? 'is-selected' : ''} onClick={() => setPlayerSheetMode('member')}>{memberText("portalPresentation.member_member")}</button>{allowGuests ? <button type="button" role="tab" aria-selected={playerSheetMode === 'guest'} className={playerSheetMode === 'guest' ? 'is-selected' : ''} onClick={() => setPlayerSheetMode('guest')}>{memberText("portalPresentation.member_guest")}</button> : null}</div>{playerSheetMode === 'member' ? <div className="mp-racquet-player-sheet__body"><label className="mp-racquet-mobile-field"><span>{memberText("portalPresentation.find_a_member")}</span><input autoFocus value={playerDraft.name} onChange={(event) => { setPlayerDraft((draft) => ({ ...draft, name: event.target.value })); onPlayerSearch(event.target.value) }} placeholder={memberText("portalPresentation.member_search_by_name")} /></label>{memberSearchLoading ? <div className="mp-racquet-player-search-skeleton" aria-label={memberText("portalPresentation.member_searching_members")}><i /><i /><i /></div> : <div className="mp-racquet-player-results">{memberSuggestions.map((result) => { const name = (result.full_name || [result.first_name, result.last_name].filter(Boolean).join(' ')).trim(); return <button type="button" key={result.id} onClick={() => addSheetMember(result)}><span>{name.charAt(0)}</span><strong>{name}</strong><small>{memberText("portalPresentation.member_member")}</small></button> })}</div>}</div> : <div className="mp-racquet-player-sheet__body"><label className="mp-racquet-mobile-field"><span>{memberText("portalPresentation.member_guest_name")}</span><input autoFocus value={playerDraft.name} onChange={(event) => setPlayerDraft((draft) => ({ ...draft, name: event.target.value }))} /></label><label className="mp-racquet-mobile-field"><span>{memberText("portalPresentation.member_email")}</span><input type="email" value={playerDraft.guestEmail} onChange={(event) => setPlayerDraft((draft) => ({ ...draft, guestEmail: event.target.value }))} /></label><label className="mp-racquet-mobile-field"><span>{memberText("portalPresentation.member_phone")}</span><input type="tel" value={playerDraft.guestPhone} onChange={(event) => setPlayerDraft((draft) => ({ ...draft, guestPhone: event.target.value }))} /></label><button type="button" className="mp-racquet-player-sheet__add" disabled={!playerDraft.name.trim() || !playerDraft.guestEmail.trim() || !playerDraft.guestPhone.trim()} onClick={addSheetGuest}>{memberText("racquetPlay.addGuest")}</button></div>}</section></div> : null}
    </form>
  )
}

export default function MemberRacquetBookingConfirmPage({ clubCode, member, isMobileApp = false }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const navigate = useNavigate()
  const location = useLocation()
  const { sportType: sportTypeParam } = useParams()
  const [searchParams] = useSearchParams()
  const { memberPortalApiGet, memberPortalApiPost } = useMemberPortalApi()
  const isMobileBooking = isMobileApp === true

  const sportType = useMemo(() => String(sportTypeParam || '').toLowerCase(), [sportTypeParam])

  useEffect(() => subscribePayNowCompleted({
    module: 'racquet',
    clubCode,
    onCompleted: (data) => navigate(`/${clubCode}/book-racquet/${sportType}/booking/success?booking_id=${data.booking_id}`, { replace: true }),
  }), [clubCode, navigate, sportType])

  const sportLabel = useMemo(() => formatSportTypeLabel(sportType, memberText), [sportType, memberText])
  const isValidSport = MEMBER_RACQUET_SPORTS.includes(sportType)

  const courtIdParam = searchParams.get('courtId') || searchParams.get('court')
  const dateParam = searchParams.get('date')
  const startParam = searchParams.get('start') || searchParams.get('startMinutes')
  const durationParam = searchParams.get('duration')

  const initialDurationFromQuery = useMemo(() => {
    const parsed = Number.parseInt(durationParam, 10)
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null
  }, [durationParam])

  const initialDate = useMemo(() => {
    const fromQuery = parseDateParam(dateParam)
    if (fromQuery) return fromQuery
    if (location.state?.date instanceof Date && !Number.isNaN(location.state.date.getTime())) {
      return startOfDay(location.state.date)
    }
    const dateFromState = parseDateParam(location.state?.date)
    return dateFromState || startOfDay(new Date())
  }, [dateParam, location.state])

  const startMinutesFromState = useMemo(() => {
    if (Number.isFinite(location.state?.startMinutes)) return location.state.startMinutes
    if (location.state?.start instanceof Date && !Number.isNaN(location.state.start.getTime())) {
      const d = location.state.start
      return d.getHours() * 60 + d.getMinutes()
    }
    return null
  }, [location.state])

  const startMinutesFromParams = useMemo(() => parseTimeToMinutes(startParam), [startParam])

  const [selectedDate] = useState(initialDate)
  const [selectedDuration, setSelectedDuration] = useState(() => {
    if (initialDurationFromQuery) return initialDurationFromQuery
    if (Number.isFinite(location.state?.duration) && Number(location.state.duration) > 0) {
      return Number(location.state.duration)
    }
    return Number(location.state?.court?.default_duration_minutes) || 60
  })
  const durationPinnedRef = useRef(Boolean(initialDurationFromQuery))

  const [court, setCourt] = useState(location.state?.court || null)
  const [facilityImage, setFacilityImage] = useState(location.state?.facilityImage || null)
  const [addons, setAddons] = useState([])
  const [selectedAddonsById, setSelectedAddonsById] = useState({})
  const [currencyCode, setCurrencyCode] = useState('GBP')
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('member_account')
  const [submitError, setSubmitError] = useState(null)
  const [formMessage, setFormMessage] = useState(null)
  const [quotedTotal, setQuotedTotal] = useState(null)
  const [quoteLoading, setQuoteLoading] = useState(false)
  const [quotePlayers, setQuotePlayers] = useState([])
  const [memberResultsByIndex, setMemberResultsByIndex] = useState({})
  const [memberSearchLoadingByIndex, setMemberSearchLoadingByIndex] = useState({})
  const playerSheetTriggerRef = useRef(null)
  const [playerSheet, setPlayerSheet] = useState(false)
  const [playerSheetMode, setPlayerSheetMode] = useState('member')
  const [playerDraft, setPlayerDraft] = useState(() => buildPlayer())
  const [bookingKind, setBookingKind] = useState('private')
  const [openMatchJoinPolicy, setOpenMatchJoinPolicy] = useState('approval_required')
  const [openMatchType, setOpenMatchType] = useState('friendly')
  const [openMatchNote, setOpenMatchNote] = useState('')
  const [skillMin, setSkillMin] = useState('')
  const [skillMax, setSkillMax] = useState('')
  const memberSearchControllersRef = useRef({})
  const memberSearchTimersRef = useRef({})
  const resolvedCourtSize = useMemo(() => {
    const size = court?.court_size ?? court?.courtSize
    return typeof size === 'string' ? size.trim().toLowerCase() : ''
  }, [court?.court_size, court?.courtSize])
  const maxPlayers = useMemo(() => getRacquetMaxPlayersClient(resolvedCourtSize), [resolvedCourtSize])
  const allowGuests = court?.non_members_allowed === true || court?.nonMembersAllowed === true
  const resolvedMemberId = useMemo(() => resolveMemberId(member), [member])

  const primaryName = useMemo(
    () =>
      [member?.first_name || member?.firstName, member?.last_name || member?.lastName]
        .filter(Boolean)
        .join(' ') || member?.full_name || member?.fullName || 'Primary member',
    [member?.firstName, member?.first_name, member?.fullName, member?.full_name, member?.lastName, member?.last_name]
  )

  const [players, setPlayers] = useState(() => [
    buildPlayer({ name: primaryName, nameIsFallback: ![member?.first_name, member?.firstName, member?.last_name, member?.lastName, member?.full_name, member?.fullName].some(Boolean), member_id: resolvedMemberId, locked: true, type: 'member' }),
  ])

  useEffect(() => {
    if (!resolvedMemberId) return

    setPlayers((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev
      const first = prev[0]
      if (normalizeUuidLike(first?.member_id)) return prev
      return [{ ...first, member_id: resolvedMemberId, type: 'member', locked: true }, ...prev.slice(1)]
    })
  }, [resolvedMemberId])

  const activePlayers = useMemo(() => {
    return players.slice(0, maxPlayers || 2)
  }, [maxPlayers, players])

  useEffect(() => {
    setPlayers((prev) => prev.slice(0, maxPlayers || 2))
    setMemberResultsByIndex((prev) => {
      const next = {}
      Object.entries(prev).forEach(([key, value]) => {
        const idx = Number.parseInt(key, 10)
        if (Number.isFinite(idx) && idx < (maxPlayers || 2)) {
          next[idx] = value
        }
      })
      return next
    })
  }, [maxPlayers])

  const handleDurationSelect = (value) => {
    durationPinnedRef.current = true
    setSelectedDuration(value)
  }

  const startDateTime = useMemo(() => {
    if (location.state?.start instanceof Date && !Number.isNaN(location.state.start.getTime())) {
      return location.state.start
    }
    const minutes = Number.isFinite(startMinutesFromParams) ? startMinutesFromParams : startMinutesFromState
    if (!selectedDate || !Number.isFinite(minutes)) return null
    return minutesToDate(selectedDate, minutes)
  }, [location.state?.start, selectedDate, startMinutesFromParams, startMinutesFromState])

  const endDateTime = useMemo(() => {
    if (!startDateTime) return null
    return new Date(startDateTime.getTime() + selectedDuration * 60 * 1000)
  }, [selectedDuration, startDateTime])

  useEffect(() => {
    let cancelled = false

    async function loadBookings() {
      if (!clubCode || !selectedDate || !courtIdParam) {
        setBookings([])
        return
      }

      const dateKey = formatDateParam(selectedDate)
      if (!dateKey) {
        setBookings([])
        return
      }

      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/racquet/bookings?date=${dateKey}&court_id=${courtIdParam}`
        )
        const payload = await res.json().catch(() => null)
        if (cancelled) return
        if (!res.ok || !payload?.ok) {
          setBookings([])
          return
        }
        setBookings(Array.isArray(payload?.data) ? payload.data : [])
      } catch (err) {
        if (!cancelled) {
          setBookings([])
        }
      }
    }

    loadBookings()

    return () => {
      cancelled = true
    }
  }, [clubCode, courtIdParam, memberPortalApiGet, selectedDate])

  const maxAllowedMinutes = useMemo(() => {
    if (!startDateTime || !selectedDate) return null

    const startMs = startDateTime.getTime()
    const nextBookingStart = bookings
      .filter((booking) => {
        const sameCourt = String(booking?.court_id) === String(court?.id || courtIdParam)
        const isConfirmed = String(booking?.status || '').toLowerCase() === 'confirmed'
        const bookingStart = new Date(booking?.start_at)
        return (
          sameCourt &&
          isConfirmed &&
          !Number.isNaN(bookingStart.getTime()) &&
          bookingStart.getTime() > startMs
        )
      })
      .map((booking) => new Date(booking.start_at))
      .sort((a, b) => a.getTime() - b.getTime())[0]

    const closingMinutes = parseTimeToMinutes(
      court?.bookable_end_time || court?.booking_hours_end || null
    )
    const closingDateTime = Number.isFinite(closingMinutes)
      ? minutesToDate(selectedDate, closingMinutes)
      : null
    const endOfDay = new Date(startOfDay(selectedDate).getTime() + 24 * 60 * 60 * 1000)

    const maxFromNext = nextBookingStart ? diffMinutes(startDateTime, nextBookingStart) : null
    const closingTarget =
      closingDateTime && closingDateTime > startDateTime ? closingDateTime : endOfDay
    const maxFromClosing = closingTarget ? diffMinutes(startDateTime, closingTarget) : null

    if (maxFromNext == null && maxFromClosing == null) return null
    const candidates = [maxFromNext, maxFromClosing].filter((value) => Number.isFinite(value))
    if (!candidates.length) return null
    return Math.max(Math.min(...candidates), 0)
  }, [bookings, court?.bookable_end_time, court?.booking_hours_end, court?.id, courtIdParam, selectedDate, startDateTime])

  const validDurationOptions = useMemo(() => deriveMemberPortalRacquetDurationOptions({
    court,
    maxAllowedMinutes,
  }), [court, maxAllowedMinutes])
  const selectedDurationIsValid = useMemo(
    () => isValidMemberPortalRacquetDurationSelection(selectedDuration, validDurationOptions),
    [selectedDuration, validDurationOptions]
  )

  useEffect(() => {
    if (selectedDurationIsValid) return
    const nextDuration = getPreferredMemberPortalRacquetDuration({
      requestedDuration: selectedDuration,
      court,
      durationOptions: validDurationOptions,
    })
    if (nextDuration != null && nextDuration !== selectedDuration) {
      setSelectedDuration(nextDuration)
    }
  }, [court, selectedDuration, selectedDurationIsValid, validDurationOptions])

  const summary = useMemo(
    () => ({
      courtName: court?.name || memberText("portalPresentation.member_select_a_court"),
      dateLabel: formatDateLabel(selectedDate, locale, memberText),
      timeLabel: startDateTime ? `${formatTimeLabel(startDateTime, locale, memberText)} – ${formatTimeLabel(endDateTime, locale, memberText)}` : memberText("portalPresentation.member_select_time"),
      durationLabel: formatDuration(selectedDuration, locale, memberText),
    }),
    [court?.name, endDateTime, selectedDate, selectedDuration, startDateTime, locale, memberText]
  )

  const totalCost = useMemo(
    () => (Number.isFinite(Number(quotedTotal)) ? Number(quotedTotal) : 0),
    [quotedTotal]
  )

  const formatPrice = (value) => {
    const amount = Number.isFinite(Number(value)) ? Number(value) : 0
    return formatCurrencyAmount(amount, currencyCode, { locale })
  }

  const playerErrors = useMemo(() => {
    const errs = []
    activePlayers.forEach((player, idx) => {
      const trimmed = player?.name?.trim()
      if (!trimmed) {
        if (idx === 0) errs[idx] = "portalPresentation.member_member_name_required"
        return
      }
      if (idx === 0 && resolvedMemberId && !normalizeUuidLike(player?.member_id)) {
        errs[idx] = "portalPresentation.member_primary_member_required"
        return
      }
      if (player?.type === 'guest') {
        if (!allowGuests) {
          errs[idx] = "portalPresentation.member_guests_are_not_allowed_on_this_court"
        } else if (!player?.guestPhone || !player?.guestEmail) {
          errs[idx] = "portalPresentation.member_guest_email_and_phone_are_required"
        }
      } else if (idx > 0 && !player?.member_id) {
        errs[idx] = "portalPresentation.member_select_a_member_from_the_list"
      }
    })
    return errs
  }, [activePlayers, allowGuests, resolvedMemberId])

  const validationErrors = useMemo(() => {
    const errs = []
    if (!isValidSport) errs.push("portalPresentation.member_sport_not_supported")
    if (!court) errs.push("portalPresentation.member_court_details_not_loaded")
    if (!startDateTime) errs.push("portalPresentation.member_start_time_missing")
    if (playerErrors.some(Boolean)) errs.push("portalPresentation.member_please_resolve_player_details")
    return errs
  }, [court, isValidSport, playerErrors, startDateTime])

  useEffect(() => {
    if (durationPinnedRef.current) return
    const courtDefault = Number(court?.default_duration_minutes)
    if (Number.isInteger(courtDefault) && courtDefault > 0) {
      setSelectedDuration(courtDefault)
    }
  }, [court?.default_duration_minutes])

  useEffect(() => {
    let cancelled = false

    async function loadCourt() {
      if (!clubCode || !courtIdParam || !isValidSport) return
      setLoading(true)
      setError(null)
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/racquet/courts/${courtIdParam}?sport_type=${sportType}`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_load_court')
        if (!cancelled) {
          setCourt(json?.data || null)
          if (json?.currency_code) setCurrencyCode(json.currency_code)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load racquet court', err)
          setError("portalPresentation.member_unable_to_load_court_details_right_now")
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    if (courtIdParam) {
      loadCourt()
    } else {
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [clubCode, courtIdParam, isValidSport, memberPortalApiGet, sportType])

  useEffect(() => {
    let cancelled = false

    async function loadAddons() {
      if (!clubCode || !isValidSport) return
      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/racquet/addons?sport_type=${sportType}`
        )
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'unable_to_load_addons')
        if (!cancelled) {
          setAddons(Array.isArray(json?.data) ? json.data : [])
          if (json?.currency_code) setCurrencyCode(json.currency_code)
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Unable to load racquet addons', err)
          setAddons([])
        }
      }
    }

    loadAddons()

    return () => {
      cancelled = true
    }
  }, [clubCode, isValidSport, memberPortalApiGet, sportType])

  useEffect(() => {
    let cancelled = false

    async function loadFacilityImage() {
      if (facilityImage || !clubCode) return
      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/facilities`)
        const json = await res.json().catch(() => null)
        if (!res.ok) throw new Error(json?.error || 'facilities_fetch_failed')
        const facilities = Array.isArray(json?.data) ? json.data : []
        const match = facilities.find((facility) => facility?.facility_key === sportType)
        if (!cancelled && match?.image_url) {
          setFacilityImage(match.image_url)
        }
      } catch (err) {
        if (!cancelled) console.warn('Failed to load facility image', err)
      }
    }

    loadFacilityImage()

    return () => {
      cancelled = true
    }
  }, [clubCode, facilityImage, memberPortalApiGet, sportType])

  useEffect(
    () => () => {
      Object.values(memberSearchControllersRef.current).forEach((controller) => controller?.abort?.())
      Object.values(memberSearchTimersRef.current).forEach((timer) => clearTimeout(timer))
    },
    []
  )

  const updatePlayer = (index, updates) => {
    setPlayers((prev) => prev.map((player, idx) => (idx === index ? { ...player, ...updates } : player)))
  }

  const addPlayer = () => {
    setPlayers((prev) => {
      if (prev.length >= (maxPlayers || 2)) return prev
      return [...prev, buildPlayer()]
    })
  }

  const removePlayer = (index) => {
    setPlayers((prev) => prev.filter((_, idx) => idx !== index))
    setMemberResultsByIndex((prev) => {
      const next = { ...prev }
      delete next[index]
      return next
    })
  }

  const triggerMemberSearch = (query, index) => {
    const trimmed = String(query || '').trim()
    if (trimmed.length < 2) {
      setMemberResultsByIndex((prev) => ({ ...prev, [index]: [] }))
      setMemberSearchLoadingByIndex((prev) => ({ ...prev, [index]: false }))
      return
    }

    if (memberSearchTimersRef.current[index]) {
      clearTimeout(memberSearchTimersRef.current[index])
    }

    memberSearchTimersRef.current[index] = setTimeout(async () => {
      if (memberSearchControllersRef.current[index]) {
        memberSearchControllersRef.current[index].abort()
      }

      const controller = new AbortController()
      memberSearchControllersRef.current[index] = controller
      setMemberSearchLoadingByIndex((prev) => ({ ...prev, [index]: true }))

      try {
        const res = await memberPortalApiGet(
          `/api/members-portal/${clubCode}/members/search?q=${encodeURIComponent(trimmed)}`,
          { signal: controller.signal }
        )
        const json = await res.json().catch(() => null)
        if (res.ok && Array.isArray(json?.data)) {
          setMemberResultsByIndex((prev) => ({ ...prev, [index]: json.data }))
        } else {
          setMemberResultsByIndex((prev) => ({ ...prev, [index]: [] }))
        }
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.warn('Member search failed', err)
        }
      } finally {
        setMemberSearchLoadingByIndex((prev) => ({ ...prev, [index]: false }))
      }
    }, 200)
  }

  const findMemberMatch = (value, index) => {
    const trimmed = String(value || '').trim().toLowerCase()
    if (!trimmed) return null
    const results = memberResultsByIndex[index] || []
    return results.find((m) => {
      const fullName = (m.full_name || [m.first_name, m.last_name].filter(Boolean).join(' '))
        .trim()
        .toLowerCase()
      return fullName && fullName === trimmed
    })
  }

  const handlePlayerNameChange = (index, name) => {
    const matchedMember = findMemberMatch(name, index)
    const trimmed = String(name || '').trim()
    setPlayers((prev) =>
      prev.map((player, idx) => {
        if (idx !== index) return player
        const isLocked = player.locked
        const resolvedType =
          isLocked || index === 0
            ? 'member'
            : matchedMember
              ? 'member'
              : trimmed
                ? allowGuests
                  ? 'guest'
                  : 'member'
                : null
        const shouldClearGuest = resolvedType !== 'guest'
        return {
          ...player,
          name,
          type: resolvedType || 'member',
          member_id:
            resolvedType === 'member'
              ? index === 0
                ? player.member_id || resolvedMemberId || null
                : matchedMember?.id || null
              : null,
          guestEmail: shouldClearGuest ? '' : player.guestEmail,
          guestPhone: shouldClearGuest ? '' : player.guestPhone,
        }
      })
    )
    triggerMemberSearch(name, index)
  }

  const handleSelectMember = (index, memberRow) => {
    const fullName = (memberRow?.full_name || [memberRow?.first_name, memberRow?.last_name].filter(Boolean).join(' ')).trim()
    updatePlayer(index, {
      name: fullName,
      member_id: memberRow?.id || null,
      type: 'member',
      guestEmail: '',
      guestPhone: '',
    })
    setMemberResultsByIndex((prev) => ({ ...prev, [index]: [] }))
  }

  const mobileSheetIndex = activePlayers.length
  const openPlayerSheet = (event) => {
    playerSheetTriggerRef.current = event?.currentTarget
    if (activePlayers.length >= maxPlayers) return
    setPlayerDraft(buildPlayer())
    setPlayerSheetMode('member')
    setMemberResultsByIndex((prev) => ({ ...prev, [mobileSheetIndex]: [] }))
    setPlayerSheet(true)
  }
  const closePlayerSheet = () => {
    setPlayerSheet(false)
    playerSheetTriggerRef.current?.focus({ preventScroll: true })
    setPlayerDraft(buildPlayer())
    setMemberResultsByIndex((prev) => ({ ...prev, [mobileSheetIndex]: [] }))
  }
  const addSheetMember = (memberRow) => {
    const fullName = (memberRow?.full_name || [memberRow?.first_name, memberRow?.last_name].filter(Boolean).join(' ')).trim()
    if (!fullName || !memberRow?.id || activePlayers.length >= maxPlayers) return
    setPlayers((prev) => [...prev, buildPlayer({ name: fullName, member_id: memberRow.id, type: 'member' })].slice(0, maxPlayers))
    closePlayerSheet()
  }
  const addSheetGuest = () => {
    if (!allowGuests || activePlayers.length >= maxPlayers) return
    const name = playerDraft.name.trim()
    const guestEmail = playerDraft.guestEmail.trim()
    const guestPhone = playerDraft.guestPhone.trim()
    if (!name || !guestEmail || !guestPhone) return
    setPlayers((prev) => [...prev, buildPlayer({ name, type: 'guest', guestEmail, guestPhone })].slice(0, maxPlayers))
    closePlayerSheet()
  }

  const incrementAddon = (id) => {
    setSelectedAddonsById((prev) => ({ ...prev, [id]: (Number.parseInt(prev?.[id], 10) || 0) + 1 }))
  }

  const decrementAddon = (id) => {
    setSelectedAddonsById((prev) => {
      const current = Number.parseInt(prev?.[id], 10) || 0
      const next = Math.max(current - 1, 0)
      return { ...prev, [id]: next }
    })
  }

  useEffect(() => {
    let cancelled = false

    async function loadQuote() {
      if (!clubCode || !court?.id || !startDateTime || !selectedDuration || !isValidSport) {
        setQuotedTotal(null)
        return
      }

      const payloadPlayers = activePlayers
        .map((player, index) => ({
          name: player?.name?.trim(),
          player_type: player?.type === 'guest' ? 'guest' : 'member',
          member_id: index === 0 ? player.member_id || resolvedMemberId : player.member_id,
          guest_email: player?.guestEmail,
          guest_phone: player?.guestPhone,
        }))
        .filter((p) => p.name)

      if (!payloadPlayers.length) {
        setQuotedTotal(null)
        setQuotePlayers([])
        return
      }

      const payloadAddons = addons
        .map((addon) => ({
          id: addon.id,
          quantity: Number.parseInt(selectedAddonsById?.[addon.id], 10) || 0,
        }))
        .filter((addon) => addon.id && addon.quantity > 0)

      setQuoteLoading(true)
      try {
        const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/racquet/quote`, {
          court_id: String(court.id),
          sport_type: String(sportType || ''),
          start_at: startDateTime.toISOString(),
          duration_minutes: String(selectedDuration),
          players: payloadPlayers,
          addons: payloadAddons,
        })
        const json = await res.json().catch(() => null)
        if (cancelled) return
        if (!res.ok || !json?.ok) {
          setQuotedTotal(null)
          setQuotePlayers([])
          return
        }

        const pricingData = json?.data || json?.pricing || null
        const nextTotal = Number(pricingData?.final_total ?? pricingData?.finalTotal)
        setQuotedTotal(Number.isFinite(nextTotal) ? nextTotal : null)
        setQuotePlayers(Array.isArray(pricingData?.players) ? pricingData.players : [])
        if (json?.currency_code) setCurrencyCode(json.currency_code)
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load racquet quote', err)
          setQuotedTotal(null)
          setQuotePlayers([])
        }
      } finally {
        if (!cancelled) setQuoteLoading(false)
      }
    }

    loadQuote()

    return () => {
      cancelled = true
    }
  }, [activePlayers, addons, clubCode, court?.id, isValidSport, memberPortalApiPost, resolvedMemberId, selectedAddonsById, selectedDuration, sportType, startDateTime])

  const handleSubmit = async (event) => {
    event.preventDefault()
    setSubmitError(null)
    setFormMessage(null)
    if (saving || validationErrors.length > 0) return
    if (!selectedDurationIsValid) {
      setSubmitError({ message: validDurationOptions.length ? "portalPresentation.member_please_select_a_valid_duration_for_this_court_and_time" : "portalPresentation.member_no_booking_durations_are_configured_for_this_court", canRefreshAvailability: false })
      return
    }

    const payloadPlayers = activePlayers
      .map((player, index) => ({
        name: player?.name?.trim(),
        type: player?.type === 'guest' ? 'guest' : 'member',
        member_id: index === 0 ? player.member_id || resolvedMemberId : player.member_id,
        guest_email: player?.guestEmail,
        guest_phone: player?.guestPhone,
      }))
      .filter((p) => p.name)

    const payloadAddons = addons
      .map((addon) => ({
        id: addon.id,
        quantity: Number.parseInt(selectedAddonsById?.[addon.id], 10) || 0,
      }))
      .filter((addon) => addon.id && addon.quantity > 0)

    setSaving(true)

    try {
      const bookingPayload = {
        court_id: court?.id,
        sport_type: sportType,
        start_at: startDateTime?.toISOString?.(),
        end_at: endDateTime?.toISOString?.(),
        duration_minutes: selectedDuration,
        players: payloadPlayers,
        addons: payloadAddons,
        date: dateParam,
        start_time: startParam,
        booking_kind: bookingKind,
        open_match: bookingKind === 'open_match'
          ? {
              match_type: openMatchType,
              join_policy: openMatchJoinPolicy,
              min_players: 2,
              max_players: maxPlayers || 4,
              visibility: 'club',
              skill_min: skillMin === '' ? null : Number(skillMin),
              skill_max: skillMax === '' ? null : Number(skillMax),
              note: openMatchNote?.trim() || null,
            }
          : null,
        payment_method: paymentMethod === 'pay_now' ? 'pay_now' : 'account',
        estimated_amount: Number(quotedTotal ?? totalCost ?? 0),
      }
      if (paymentMethod === 'pay_now') {
        const handoff = createPayNowHandoff({ module: 'racquet', clubCode, successPath: `/${clubCode}/book-racquet/${sportType}/booking/success`, sportType })
        const checkoutWindow = openPayNowCheckoutPlaceholder(handoff, { locale })
        if (!checkoutWindow) throw new Error('popup_blocked')
        const checkoutRes = await memberPortalApiPost(`/api/members-portal/${clubCode}/booking-payments/checkout`, { module: 'racquet', booking_payload: bookingPayload })
        const checkoutJson = await checkoutRes.json().catch(() => null)
        if (!checkoutRes.ok || !checkoutJson?.url) throw new Error(checkoutJson?.error || 'checkout_failed')
        updatePayNowHandoff(handoff.key, { checkout_session_id: checkoutJson.checkout_session_id || checkoutJson.session_id || checkoutJson.sessionId || null, payment_attempt_id: checkoutJson.payment_attempt_id || checkoutJson.paymentAttemptId || null })
        checkoutWindow.location.assign(checkoutJson.url)
        return
      }
      const res = await memberPortalApiPost(`/api/members-portal/${clubCode}/racquet/book`, bookingPayload)
      const json = await res.json().catch(() => null)

      if (res.ok && json && json.ok !== false) {
        const booking = createCommittedBooking({ id: json.booking_id, module: 'racquet', title: court?.name || sportLabel, location: court?.name,
          startAt: startDateTime?.toISOString?.(), timeZone: court?.time_zone, imageUrl: facilityImage,
          amount: Number(quotedTotal ?? totalCost), currency: currencyCode,
          details: { sportType, bookingKind, participants: payloadPlayers, openMatch: bookingKind === 'open_match' ? { match_type: openMatchType, join_policy: openMatchJoinPolicy, max_players: maxPlayers, confirmed_count: payloadPlayers.length, places_available: Math.max(0, maxPlayers - payloadPlayers.length) } : null, payment_method: 'member_account', payment_summary: 'Charged to member account' } })
        commitMemberBookingSuccess(booking, { clubCode })
        navigate(`/${clubCode}/book-racquet/${sportType}/booking/success?booking_id=${json.booking_id}`, { state: { booking } })
        return
      } else {
        const errorCode = json?.error
        const memberError = getMemberRacquetBookingError(errorCode)
        if ((errorCode === 'invalid_duration' || errorCode === 'duration_not_configured') && Array.isArray(json?.allowedDurations) && json.allowedDurations.length) {
          const nextDuration = getPreferredMemberPortalRacquetDuration({
            requestedDuration: selectedDuration,
            court,
            durationOptions: json.allowedDurations,
          })
          if (nextDuration != null) setSelectedDuration(nextDuration)
        }
        setSubmitError(memberError)
      }
    } catch (err) {
      console.error('Failed to confirm racquet booking', err)
      setSubmitError(getMemberRacquetBookingError(err?.message))
    } finally {
      setSaving(false)
    }
  }

  const backDateParam = formatDateParam(selectedDate)
  const backLink = `/${clubCode}/book-racquet/${sportType}${backDateParam ? `?date=${backDateParam}` : ''}`

  if (!isValidSport) {
    return (
      <div className="mp-panel">
        <div className="mp-panel__header">
          <div>
            <p className="mp-eyebrow">{memberText("portalPresentation.member_racquet")}</p>
            <h4 className="mp-panel__title">{memberText("portalPresentation.member_sport_not_supported")}</h4>
          </div>
        </div>
        <div className="mp-warning">{memberText("portalPresentation.member_we_couldn_t_recognize_this_sport_please_return_to_facilities")}</div>
      </div>
    )
  }

  const authoritativeDisplayTotal = Number.isFinite(Number(quotedTotal)) ? Number(quotedTotal) : null

  if (isMobileBooking) {
    return (
      <MobileRacquetBookingWorkspace
        facilityImage={facilityImage} backLink={backLink} summary={summary} sportLabel={sportLabel} sportType={sportType} court={court}
        selectedDuration={selectedDuration} bookingKind={bookingKind} setBookingKind={setBookingKind}
        openMatchType={openMatchType} setOpenMatchType={setOpenMatchType}
        openMatchJoinPolicy={openMatchJoinPolicy} setOpenMatchJoinPolicy={setOpenMatchJoinPolicy}
        openMatchNote={openMatchNote} setOpenMatchNote={setOpenMatchNote} skillMin={skillMin} setSkillMin={setSkillMin} skillMax={skillMax} setSkillMax={setSkillMax}
        activePlayers={activePlayers} maxPlayers={maxPlayers} removePlayer={removePlayer}
        openPlayerSheet={openPlayerSheet} addons={addons} selectedAddonsById={selectedAddonsById}
        incrementAddon={incrementAddon} decrementAddon={decrementAddon} formatPrice={formatPrice}
        paymentMethod={paymentMethod} setPaymentMethod={setPaymentMethod} saving={saving}
        quoteLoading={quoteLoading} authoritativeDisplayTotal={authoritativeDisplayTotal}
        validationErrors={validationErrors} submitError={submitError}
        onViewOtherTimes={() => navigate(backLink)} handleSubmit={handleSubmit}
        playerSheet={playerSheet} closePlayerSheet={closePlayerSheet}
        playerSheetMode={playerSheetMode} setPlayerSheetMode={setPlayerSheetMode}
        playerDraft={playerDraft} setPlayerDraft={setPlayerDraft}
        memberSuggestions={memberResultsByIndex[mobileSheetIndex] || []}
        memberSearchLoading={memberSearchLoadingByIndex[mobileSheetIndex] === true}
        onPlayerSearch={(query) => triggerMemberSearch(query, mobileSheetIndex)}
        addSheetMember={addSheetMember} addSheetGuest={addSheetGuest} allowGuests={allowGuests}
      />
    )
  }

  return (
    <div className="mp-tee-booking mp-racquet-booking">
      <div className="mp-tee-booking__header">
        <BackPillButton className="mp-tee-booking__back" to={backLink}> {memberText("portalPresentation.member_back_to_courts")} </BackPillButton>
        <div className="mp-tee-booking__title">
          <h3>
            <strong>{summary.courtName}</strong> – {summary.dateLabel} – {summary.timeLabel}
          </h3>
        </div>
        <div aria-hidden className="mp-tee-booking__spacer" />
      </div>

      {error && <div className="mp-warning">{memberText(error)}</div>}

      <form className="mp-tee-booking__grid" onSubmit={handleSubmit}>
        <div className="mp-tee-booking__media" aria-hidden>
          <div
            className="mp-tee-booking__image"
            style={facilityImage ? { backgroundImage: `url(${facilityImage})` } : undefined}
          />
        </div>

        <div className="mp-tee-booking__forms">
          <div className="mp-card mp-racquet-booking__details-card">
            <div className="mp-card__header">
              <div>
                <p className="mp-eyebrow">{memberText("facilityBooking.details")}</p>
                <h4>{memberText("portalPresentation.member_confirm_your_court")}</h4>
              </div>
            </div>
            <div className="mp-card__body grid two-col">
              <div>
                <p className="mp-label">{memberText("racquetPlay.court")}</p>
                <p className="mp-value">{summary.courtName}</p>
              </div>
              <div>
                <p className="mp-label">{memberText("portalPresentation.member_date")}</p>
                <p className="mp-value">{summary.dateLabel}</p>
              </div>
              <div>
                <p className="mp-label">{memberText("facilityBooking.time")}</p>
                <p className="mp-value">{summary.timeLabel}</p>
              </div>
              <div>
                <p className="mp-label">{memberText("portalPresentation.member_duration")}</p>
                <div className="mp-toggle-group" role="group" aria-label={memberText("portalPresentation.member_select_duration")}>
                  {validDurationOptions.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      className={selectedDuration === opt ? 'active' : ''}
                      onClick={() => handleDurationSelect(opt)}
                    >
                      {formatDuration(opt)}
                    </button>
                  ))}
                  {!validDurationOptions.length ? <p className="mp-warning">{memberText("portalPresentation.member_no_booking_durations_are_configured_for_this_court")}</p> : null}
                </div>
              </div>
              <div>
                <p className="mp-label">{memberText("portalPresentation.member_booking_type")}</p>
                <div className="mp-toggle-group" role="group" aria-label={memberText("portalPresentation.member_select_booking_type")}>
                  <button type="button" className={bookingKind === 'private' ? 'active' : ''} onClick={() => setBookingKind('private')}> {memberText("portalPresentation.member_book_a_court")} </button>
                  <button type="button" className={bookingKind === 'open_match' ? 'active' : ''} onClick={() => setBookingKind('open_match')}> {memberText("portalPresentation.member_create_open_match")} </button>
                </div>
              </div>
              {bookingKind === 'open_match' ? (
                <>
                  <div>
                    <p className="mp-label">{memberText("portalPresentation.member_match_type")}</p>
                    <div className="mp-toggle-group" role="group" aria-label={memberText("portalPresentation.member_select_open_match_type")}>
                      <button type="button" className={openMatchType === 'friendly' ? 'active' : ''} onClick={() => setOpenMatchType('friendly')}>{memberText("portalPresentation.member_friendly")}</button>
                      <button type="button" className={openMatchType === 'competitive' ? 'active' : ''} onClick={() => setOpenMatchType('competitive')}>{memberText("portalPresentation.member_competitive")}</button>
                    </div>
                  </div>
                  <div>
                    <p className="mp-label">{memberText("portalPresentation.member_who_can_join")}</p>
                    <div className="mp-toggle-group" role="group" aria-label={memberText("portalPresentation.member_select_join_policy")}>
                      <button type="button" className={openMatchJoinPolicy === 'approval_required' ? 'active' : ''} onClick={() => setOpenMatchJoinPolicy('approval_required')}>{memberText("portalPresentation.member_host_approval")}</button>
                      <button type="button" className={openMatchJoinPolicy === 'direct_join' ? 'active' : ''} onClick={() => setOpenMatchJoinPolicy('direct_join')}>{memberText("portalPresentation.member_direct_join")}</button>
                    </div>
                  </div>
                  <RacquetRangeControl sport={sportType} min={skillMin} max={skillMax} onMinChange={setSkillMin} onMaxChange={setSkillMax} />
                  <div>
                    <p className="mp-label">{memberText("portalPresentation.member_open_match_note")}</p>
                    <label className="mp-input">
                      <input type="text" maxLength={180} value={openMatchNote} onChange={(e) => setOpenMatchNote(e.target.value)} placeholder={memberText("portalPresentation.member_optional_details_for_players_joining")} />
                    </label>
                  </div>
                </>
              ) : null}
            </div>
          </div>

          <div className="mp-card mp-racquet-booking__players-card">
            <div className="mp-card__header mp-card__header--compact">
              <div className="mp-flex mp-flex--between">
                <h4>{memberText("portalPresentation.member_players_caption")}</h4>
                <button type="button" className="mp-btn mp-btn--ghost" disabled>
                  {bookingKind === 'open_match' ? memberText("portalPresentation.member_open_match_enabled") : memberText("portalPresentation.member_private_booking")}
                </button>
              </div>
            </div>
            <div className="mp-card__body">
              {activePlayers.map((player, index) => {
                const memberSuggestions = memberResultsByIndex[index] || []
                const statusLabel =
                  player.type === 'guest' ? memberText("portalPresentation.member_guest") : player.type === 'member' ? memberText("portalPresentation.member_member") : memberText("portalPresentation.member_add_name")
                const showGuestFields = allowGuests && player.type === 'guest'

                return (
                  <div key={`player-${index}`} className="mp-player-row">
                    <div className="mp-player-row__header">
                      <span className="mp-label">{memberText("portalPresentation.member_player_caption")} {index + 1}</span>
                      <div className="mp-player-row__header-actions">
                        <div className={`mp-player-status ${player.type || 'neutral'}`}>{statusLabel}</div>
                        {index > 0 && (
                          <button type="button" className="mp-link" onClick={() => removePlayer(index)}> {memberText("portalPresentation.member_remove")} </button>
                        )}
                      </div>
                    </div>
                    <div className="mp-player-row__inputs">
                      <label className="mp-input">
                        <span className="mp-label">{memberText("portalPresentation.member_name")}</span>
                        <input
                          type="text"
                          value={player.nameIsFallback ? memberText("portalPresentation.member_primary_member") : player.name}
                          onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                          onBlur={() => {
                            const suggestion = findMemberMatch(player.name, index)
                            if (suggestion) handleSelectMember(index, suggestion)
                          }}
                          placeholder={
                            player.locked
                              ? memberText("portalPresentation.member_member_on_file")
                              : allowGuests
                                ? memberText("portalPresentation.member_search_member_or_enter_guest_name")
                                : memberText("portalPresentation.member_search_and_select_a_member")
                          }
                          required={index === 0}
                          disabled={player.locked}
                        />
                        {memberSuggestions.length > 0 && (
                          <div className="mp-tee-booking__suggestions" role="listbox">
                            {memberSuggestions.map((m) => {
                              const fullName = (m.full_name || [m.first_name, m.last_name].filter(Boolean).join(' ')).trim()
                              return (
                                <button
                                  type="button"
                                  key={m.id}
                                  className="mp-tee-booking__suggestion"
                                  onClick={() => handleSelectMember(index, m)}
                                >
                                  {fullName}
                                </button>
                              )
                            })}
                          </div>
                        )}
                      </label>
                      {showGuestFields && (
                        <div className="mp-guest-inline">
                          <label className="mp-input">
                            <span className="mp-label">{memberText("portalPresentation.member_guest_phone")}</span>
                            <input
                              type="tel"
                              value={player.guestPhone}
                              onChange={(e) => updatePlayer(index, { guestPhone: e.target.value })}
                              placeholder={memberText("portalPresentation.member_mobile_for_updates")}
                              required
                            />
                          </label>
                          <label className="mp-input">
                            <span className="mp-label">{memberText("golf.bookingModal.guestEmail")}</span>
                            <input
                              type="email"
                              value={player.guestEmail}
                              onChange={(e) => updatePlayer(index, { guestEmail: e.target.value })}
                              placeholder={memberText("portalPresentation.member_email_for_confirmation")}
                              required
                            />
                          </label>
                        </div>
                      )}
                    </div>
                    {playerErrors[index] && <p className="mp-inline-error">{memberText(playerErrors[index])}</p>}
                  </div>
                )
              })}
              <div className="mp-player-actions">
                {activePlayers.length < (maxPlayers || 2) ? (
                  <button type="button" className="mp-btn mp-btn--ghost" onClick={addPlayer}> {memberText("portalPresentation.member__add_player")} </button>
                ) : (
                  <p className="mp-text-muted">{memberText("portalPresentation.member_maximum_players_reached_for_this_court")}</p>
                )}
              </div>
            </div>
          </div>

          <div className={`mp-card mp-racquet-booking__addons-card${addons.length === 0 ? ' is-empty' : ''}`}>
            <div className="mp-card__header">
              <div>
                <p className="mp-eyebrow">{memberText("portalPresentation.member_add_ons")}</p>
                <h4>{memberText("portalPresentation.member_enhance_your_visit")}</h4>
              </div>
            </div>
            <div className="mp-card__body mp-addon-list">
              {addons.length === 0 ? <p className="mp-text-muted">{memberText("portalPresentation.member_no_add_ons_available_for_this_sport")}</p> : addons.map((addon) => (
                  <div
                    key={addon.id}
                    className={`mp-addon${(selectedAddonsById?.[addon.id] || 0) > 0 ? ' selected' : ''}`}
                  >
                    <div className="mp-addon__meta">
                      <span>{addon.label || addon.name}</span>
                      {addon.price != null && <span className="mp-text-muted">{formatPrice(addon.price)}</span>}
                    </div>
                    <div className="mp-addon__actions">
                      <button
                        type="button"
                        className="mp-btn mp-btn--ghost"
                        onClick={() => decrementAddon(addon.id)}
                        disabled={(Number.parseInt(selectedAddonsById?.[addon.id], 10) || 0) <= 0}
                      >
                        -
                      </button>
                      <span className="mp-addon__qty">{Number.parseInt(selectedAddonsById?.[addon.id], 10) || 0}</span>
                      <button
                        type="button"
                        className="mp-btn mp-btn--ghost"
                        onClick={() => incrementAddon(addon.id)}
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        <aside className="mp-tee-booking__summary">
          <div className="mp-summary-card">
            <div className="mp-summary-header">
              <p className="mp-eyebrow">{memberText("facilityBooking.review")}</p>
              <h4>{memberText("portalPresentation.member_racquet_booking")}</h4>
            </div>
            <div className="mp-summary-section">
              <div className="mp-summary-row">
                <Users size={16} />
                <div>
                  <p className="mp-label">{memberText("portalPresentation.member_players_caption")}</p>
                  <p className="mp-value">{activePlayers.filter((p) => p.name?.trim()).length} {memberText("portalPresentation.member_confirmed")}</p>
                </div>
              </div>
              <div className="mp-summary-row">
                <Clock size={16} />
                <div>
                  <p className="mp-label">{memberText("facilityBooking.time")}</p>
                  <p className="mp-value">{summary.timeLabel}</p>
                </div>
              </div>
              <div className="mp-summary-row">
                <Shield size={16} />
                <div>
                  <p className="mp-label">{memberText("portalPresentation.member_duration")}</p>
                  <p className="mp-value">{summary.durationLabel}</p>
                </div>
              </div>
              <div className="mp-summary-row">
                <Shield size={16} />
                <div>
                  <p className="mp-label">{memberText("portalPresentation.member_add_ons")}</p>
                  <p className="mp-value">
                    {addons.some((addon) => Number.parseInt(selectedAddonsById?.[addon.id], 10) > 0)
                      ? addons
                          .map((addon) => {
                            const qty = Number.parseInt(selectedAddonsById?.[addon.id], 10) || 0
                            if (qty <= 0) return null
                            return `${addon.label || addon.name} x${qty}`
                          })
                          .filter(Boolean)
                          .join(', ')
                      : memberText("portalPresentation.member_none_selected")}
                  </p>
                </div>
              </div>
            </div>
            <MemberPaymentMethodOptions value={paymentMethod} onChange={setPaymentMethod} disabled={saving} />
            <div className="mp-summary-section">
              <p className="mp-label">{memberText("portalPresentation.member_estimated_total")}</p>
              <div className="mp-summary-row">
                <div>
                  <p className="mp-summary-total">{quoteLoading ? memberText("portalPresentation.member_calculating") : formatPrice(totalCost)}</p>
                  <p className="mp-text-muted">{memberText("portalPresentation.member_calculated_from_live_pricing_policy")}</p>
                </div>
              </div>
              {quotePlayers.length > 0 ? (
                <div className="mp-panel-body" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  {quotePlayers.map((player) => {
                    const label = player?.player_type === 'guest'
                      ? (player?.guest_name || player?.guest_email || memberText("portalPresentation.member_guest_count", { count: Number(player?.player_index) + 1 }))
                      : activePlayers?.[player?.player_index]?.name || memberText("portalPresentation.member_member_count", { count: Number(player?.player_index) + 1 })
                    return (
                      <div key={`quote-player-${player.player_index}`} className="mp-panel-row">
                        <div>
                          <div className="mp-row-title">{label}</div>
                          <div className="mp-row-sub">{player?.player_type === 'guest' ? memberText("portalPresentation.member_guest") : memberText("portalPresentation.member_member")}</div>
                        </div>
                        <div className="mp-row-title">{formatPrice(Number(player?.amount_after_policy || 0))}</div>
                      </div>
                    )
                  })}
                </div>
              ) : null}
            </div>

            {validationErrors.length > 0 && (
              <div className="mp-inline-error">
                {validationErrors.map((msg) => (
                  <p key={msg}>{memberText(msg)}</p>
                ))}
              </div>
            )}

            {formMessage && <div className="mp-inline-success">{formMessage}</div>}
            {submitError && <div className="mp-inline-error" role="alert">
              <p>{memberText(submitError.messageKey || submitError.message)}</p>
              {submitError.canRefreshAvailability ? <button type="button" className="mp-link" onClick={() => navigate(backLink)}>{memberText("portalPresentation.member_view_other_times")}</button> : null}
            </div>}

            <button
              type="submit"
              className="mp-btn mp-btn--primary mp-btn--block mp-racquet-booking__confirm-cta"
              disabled={validationErrors.length > 0 || saving || loading}
            >
              {saving ? (isMobileBooking ? memberText("portalPresentation.member_booking") : memberText("portalPresentation.confirming")) : isMobileBooking ? (paymentMethod === 'pay_now' ? memberText("portalPresentation.member_pay_amount", { amount: formatPrice(totalCost) }) : memberText("portalPresentation.member_book_court")) : memberText("facilityBooking.confirm")}
            </button>
          </div>
        </aside>
      </form>
    </div>
  )
}
